import type { BulkImportItemStatus, BulkImportJobResponse } from "../types"

export interface BulkImportManifestFile {
  relative_path: string
  filename: string
  size: number
  content_sha256: string
}

export interface BulkImportManifest {
  rootFolderName: string
  files: BulkImportManifestFile[]
}

type HashFileFn = (file: File) => Promise<string>

export async function buildBulkImportManifest(
  files: File[],
  hashFile: HashFileFn = computeFileSha256
): Promise<BulkImportManifest> {
  const pptxFiles = files.filter((file) => file.name.toLowerCase().endsWith(".pptx"))
  const manifestFiles: BulkImportManifestFile[] = []
  for (const file of pptxFiles) {
    const relativePath = getRelativePath(file)
    manifestFiles.push({
      relative_path: relativePath,
      filename: file.name,
      size: file.size,
      content_sha256: await hashFile(file),
    })
  }

  return {
    rootFolderName: getRootFolderName(manifestFiles[0]?.relative_path ?? ""),
    files: manifestFiles,
  }
}

export async function computeFileSha256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const hash = await crypto.subtle.digest("SHA-256", buffer)
  return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, "0")).join("")
}

export function getRelativePath(file: File): string {
  const browserPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath
  return normalizeManifestPath(browserPath || file.name)
}

export function normalizeManifestPath(path: string): string {
  return path.replaceAll("\\", "/").replace(/^\/+|\/+$/g, "")
}

function getRootFolderName(relativePath: string): string {
  return relativePath.split("/").filter(Boolean)[0] ?? "Imported slides"
}

export function updateBulkImportItemStatus(
  jobResponse: BulkImportJobResponse,
  itemId: string,
  status: BulkImportItemStatus
): BulkImportJobResponse {
  return {
    ...jobResponse,
    items: jobResponse.items.map((item) => (item.id === itemId ? { ...item, status } : item)),
  }
}
